import React, { lazy, useEffect, useState } from "react";
import { validateArrayIncludes } from "../../../common/validators";
import { ORDER_STATUS_EDITABLE } from "../../../types/order";

// SERVICES
import { handleOrderProductsCreateEdit } from "../../../api/order";
// APP COMPONENTS
import LoadingBox from "../../indicators/LoadingBox";
import ModalHeader from "../../modals/modalHeader";
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, styled, useTheme } from "@mui/material/styles";
// ICONS
import NotesIcon from "@mui/icons-material/Notes";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DinnerDiningIcon from "@mui/icons-material/DinnerDining";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { getWeightAdornment } from "../../../extras";
import { formatFinance } from "../../../utils/formatters";
import { ButtonNoShadow } from "../../styled/button";
import { CustomModalRoot } from "../../styled/modal";

// LAZY IMPORTS
const ProductsForm = lazy(() => import("./NewOrderModal/productsForm"));

// Styled components
const ProductItem = styled(Paper)(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  transition: "border-color 0.15s ease",
  "&:hover": {
    borderColor: theme.palette.grey[300],
  },
}));

const ComplementItem = styled(Box)(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  marginLeft: theme.spacing(4),
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

const ComplementCategory = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.8rem",
  color: theme.palette.text.secondary,
  paddingLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  letterSpacing: "0.5px",
  textTransform: "uppercase",
}));

const Observation = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.light, 0.1),
  border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
  borderRadius: 4,
  padding: theme.spacing(0.75, 1.5),
  marginTop: theme.spacing(0.75),
  marginLeft: theme.spacing(4),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const QuantityBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.dark,
  fontWeight: 600,
  minWidth: 36,
  height: 28,
}));

const PriceChip = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, 0.08),
  color: theme.palette.success.dark,
  fontWeight: 600,
  fontSize: "0.85rem",
  display: "inline-flex",
  alignItems: "center",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 4,
  border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  borderRadius: 6,
  backgroundColor: alpha(theme.palette.grey[100], 0.5),
  border: `1px dashed ${theme.palette.grey[400]}`,
}));

const AddButton = styled(ButtonNoShadow)(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(1, 2),
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  color: theme.palette.primary.dark,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  fontWeight: 600,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

// Products List Component
const ModernProductsList = ({ products }) => {
  const theme = useTheme();

  if (!products || products.length === 0) {
    return (
      <EmptyState>
        <ShoppingBagIcon
          sx={{ fontSize: 40, color: theme.palette.grey[400], mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" align="center">
          Nenhum produto adicionado ao pedido.
        </Typography>
      </EmptyState>
    );
  }

  let lastComplementCategory = null;

  return (
    <Box>
      {products.map((product, index) => {
        lastComplementCategory = null;

        return (
          <React.Fragment key={`product-${product.id || index}`}>
            <ProductItem elevation={0}>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", flex: 1 }}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.dark,
                      mr: 1.5,
                    }}
                  >
                    <RestaurantMenuIcon fontSize="small" />
                  </Avatar>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      {product.title !== "undefined"
                        ? product.title
                        : "Produto"}
                    </Typography>

                    <QuantityBadge
                      size="small"
                      label={
                        product.level !== 2
                          ? product.unit === "KG"
                            ? `${product.quantity} ${getWeightAdornment(
                                product.unit,
                                product.quantity
                              )}`
                            : `${Math.floor(product.quantity)} unid.`
                          : "-"
                      }
                    />
                  </Box>
                </Box>

                <PriceChip>
                  {product.total_price
                    ? formatFinance(parseFloat(product.total_price))
                    : formatFinance(
                        product.quantity *
                          (parseFloat(product.price) +
                            parseFloat(
                              (product.complementsPrice ??
                                product.options_price) ||
                                0
                            ))
                      )}
                </PriceChip>
              </Box>

              {/* Product Complements */}
              {product.complements?.map((complement, cIdx) => {
                const showCategory =
                  complement.complement_categorie &&
                  complement.complement_categorie !== lastComplementCategory;

                if (showCategory) {
                  lastComplementCategory = complement.complement_categorie;
                }

                return (
                  <React.Fragment
                    key={`complement-${product.id}-${complement.id || cIdx}`}
                  >
                    {showCategory && (
                      <ComplementCategory>
                        {complement.complement_categorie}
                      </ComplementCategory>
                    )}

                    <ComplementItem>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                            }}
                          >
                            {complement.title || "Complemento"}
                          </Typography>

                          {complement.complement_level !== 2 &&
                            complement.quantity > 0 && (
                              <Chip
                                size="small"
                                label={
                                  complement.unit === "KG"
                                    ? `${
                                        complement.quantity
                                      } ${getWeightAdornment(
                                        complement.unit,
                                        complement.quantity
                                      )}`
                                    : `${Math.floor(
                                        complement.quantity
                                      )} unid.`
                                }
                                sx={{
                                  ml: 1,
                                  height: 20,
                                  fontSize: "0.7rem",
                                  backgroundColor: alpha(
                                    theme.palette.grey[200],
                                    0.7
                                  ),
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            )}
                        </Box>

                        {(complement.price || complement.total_price) && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.success.dark,
                              fontWeight: 500,
                              fontSize: "0.8rem",
                            }}
                          >
                            {complement.total_price
                              ? formatFinance(
                                  parseFloat(complement.total_price)
                                )
                              : formatFinance(
                                  complement.quantity *
                                    parseFloat(complement.price || 0)
                                )}
                          </Typography>
                        )}
                      </Box>
                    </ComplementItem>
                  </React.Fragment>
                );
              })}

              {/* Observations */}
              {product.observations && (
                <Observation>
                  <NotesIcon
                    sx={{ fontSize: 16, color: theme.palette.warning.dark }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.primary,
                      fontStyle: "italic",
                    }}
                  >
                    {product.observations}
                  </Typography>
                </Observation>
              )}
            </ProductItem>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// Main Component
export default function ProductsInfos({
  products,
  dontShowHeader,
  orderChannel,
  orderId,
  statusNumber,
  enableEdit = true,
  updateOrder,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [editedProducts, setEditedProducts] = useState(false);
  const [productsState, setProductsState] = useState(products || []);
  const [showAddProductsModal, setShowAddProductsModal] = useState(false);

  useEffect(() => {
    if (!enableEdit) return;
    handleProductsTreat();
  }, []);

  const handleProductsTreat = () => {
    const clonedProducts = productsState.map((product) => ({ ...product }));

    const withOrderProduct = clonedProducts.filter(
      (product) => product.orderProduct
    );
    const withOrderComplement = clonedProducts.filter(
      (product) => product.orderComplement
    );

    withOrderProduct.forEach((orderProduct) => {
      const relatedComplements = withOrderComplement.filter(
        (complement) =>
          complement.complementOrderProduct === orderProduct.orderProduct
      );
      if (relatedComplements) {
        if (!orderProduct.complements) {
          orderProduct.complements = [];
        }
        orderProduct.complements = relatedComplements;
      }
    });

    setProductsState(withOrderProduct);
  };

  const handleAddProductsModal = () => {
    setShowAddProductsModal(!showAddProductsModal);

    if (enableEdit && editedProducts) {
      handleOrderProducts();
    }
  };

  const handleProducts = async (productsArray) => {
    const modifiedProductsArray = productsArray.map((product, productIndex) => {
      const modifiedProduct = { ...product, index: productIndex };

      if (product.complements && product.complements.length > 0) {
        modifiedProduct.complements = product.complements.map(
          (complement, complementIndex) => {
            return {
              ...complement,
              index: productIndex + complementIndex / 10,
            };
          }
        );
      }

      return modifiedProduct;
    });

    setProductsState(modifiedProductsArray);
    setEditedProducts(true);
  };

  const handleOrderProducts = async () => {
    try {
      setLoading(true);
      await handleOrderProductsCreateEdit(orderId, productsState);
      updateOrder();
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const totalProducts = productsState?.length || 0;
  const totalItems =
    productsState?.reduce((total, product) => {
      let count = 1;
      if (product.complements?.length) {
        count += product.complements.length;
      }
      return total + count;
    }, 0) || 0;

  return (
    <Box>
      {/* Header row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {totalProducts > 0 ? (
            <Chip
              icon={<DinnerDiningIcon />}
              label={`${totalProducts} produtos (${totalItems} itens)`}
              size="small"
              sx={{
                fontSize: "0.75rem",
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.dark,
              }}
            />
          ) : (
            <Chip
              icon={<ShoppingBagIcon />}
              label="Nenhum produto no carrinho"
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}
            />
          )}
        </Box>

        {orderChannel === "SLAPIE_POS" &&
          validateArrayIncludes(ORDER_STATUS_EDITABLE, statusNumber) && (
            <Tooltip title="Adicionar ou remover produtos">
              <AddButton
                startIcon={<AddCircleIcon />}
                onClick={handleAddProductsModal}
                size="small"
              >
                Editar
              </AddButton>
            </Tooltip>
          )}
      </Box>

      <ModernProductsList
        products={enableEdit ? productsState : products}
      />

      <Modal
        open={showAddProductsModal}
        onClose={handleAddProductsModal}
        aria-labelledby="order-info-modal-add-product"
        aria-describedby="order-info-modal-add-product"
        sx={{
          zIndex: 999999,
          "&:focus-visible": {
            border: "none",
          },
        }}
      >
        <CustomModalRoot top={50} left={50} width={60} height={75}>
          <ModalHeader
            label="Adicionar Itens"
            onClose={handleAddProductsModal}
          />
          <React.Suspense fallback={<LoadingBox />}>
            <ProductsForm
              setProducts={handleProducts}
              products={productsState}
              setModalItensTotal={() => {}}
              orderId={orderId}
            />
          </React.Suspense>
        </CustomModalRoot>
      </Modal>
    </Box>
  );
}
